/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetMatterQuery } from '@/services/api/mattersService';

export const BPOStartPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const matterId = searchParams.get('matter_id') || '';
  const toNumberQP = searchParams.get('to_number') || '';
  const workflowId = searchParams.get('workflow_id') || '';
  const runId = searchParams.get('run_id') || '';
  const coverageId = searchParams.get('coverage_id') || '';
  const taskType = searchParams.get('task_type') || '';
  const taskId = searchParams.get('task_id') || '';
  const treatmentRequestId = searchParams.get('treatment_request_id') || '';
  const approvalId = searchParams.get('approval_id') || '';

  const { data: matter } = useGetMatterQuery(matterId, { skip: !matterId });

  const [toNumber, setToNumber] = React.useState<string>(toNumberQP);
  const [fromNumber, setFromNumber] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matterId || !toNumber || !fromNumber) {
      toast.error('Please provide all required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      const qs = new URLSearchParams({
        matter_id: matterId,
        to_number: toNumber,
        from_number: fromNumber,
        redirect: '1',
        // JSON mode: return outcome URL + RC call data instead of 302 so we
        // can open the rcapp:// protocol and navigate in a single user gesture.
        json: '1',
      });
      if (workflowId) qs.set('workflow_id', workflowId);
      if (runId) qs.set('run_id', runId);
      if (coverageId) qs.set('coverage_id', coverageId);
      if (taskType) qs.set('task_type', taskType);
      if (taskId) qs.set('task_id', taskId);
      if (treatmentRequestId)
        qs.set('treatment_request_id', treatmentRequestId);
      if (approvalId) qs.set('approval_id', approvalId);

      const backend =
        (import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string) ||
        'http://localhost:8000';
      const backendRoot = backend.replace(/\/$/, '');
      const resp = await fetch(
        `${backendRoot}/api/integrations/calls/launch?${qs.toString()}`,
        { credentials: 'include' },
      );

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error((body as any)?.error || `HTTP ${resp.status}`);
      }

      const data = (await resp.json()) as {
        outcome_url: string;
        call_uri?: string;
        pending_call_id?: string;
        call_id?: string;
      };

      // For RC calls: open the desktop app protocol handler first (stays on
      // this page — custom protocols never cause page navigation), then
      // navigate to the outcome page.  Both happen within the same user
      // gesture so Chrome honours the protocol handler.
      if (data.call_uri) {
        window.open(data.call_uri, '_self');
      }
      // Small delay so the browser processes the protocol handler before navigating
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 100);
      });
      window.location.href = data.outcome_url;
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to initiate call', {
        description: err?.message || 'Please check the numbers and try again.',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl rounded-xl border border-[#1f2a44] shadow-2xl bg-gradient-to-b from-[#121936] to-[#0f1731] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#1f2a44] bg-[radial-gradient(1200px_300px_at_-10%_-20%,rgba(59,130,246,0.15),transparent_60%)]">
          <h2 className="text-lg font-semibold text-white">
            Start a Call
          </h2>
          <p className="text-xs text-[#9aa3b2] mt-1">
            Provide the numbers; we&apos;ll include the matter context for you.
          </p>
        </div>
        <div className="p-6">
          {matter && (
            <div className="mb-4 rounded-lg border border-[#1f2a44] p-3 text-sm text-[#9aa3b2] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]">
              <span className="text-white font-medium">Matter:</span>{' '}
              {matter.name}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="to_number"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                To number
              </label>
              <input
                id="to_number"
                name="to_number"
                value={toNumber}
                onChange={(e) => setToNumber(e.target.value)}
                placeholder="+1XXXXXXXXXX or (555) 555-5555"
                required
                aria-label="Destination phone number"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="from_number"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                From number
              </label>
              <input
                id="from_number"
                name="from_number"
                value={fromNumber}
                onChange={(e) => setFromNumber(e.target.value)}
                placeholder="+1XXXXXXXXXX or (555) 555-5555"
                required
                aria-label="Your caller ID"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <input type="hidden" name="matter_id" value={matterId} />
            {workflowId && (
              <input type="hidden" name="workflow_id" value={workflowId} />
            )}
            {runId && <input type="hidden" name="run_id" value={runId} />}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 disabled:opacity-50"
                aria-label="Initiate Call"
              >
                {isSubmitting ? 'Initiating…' : 'Initiate Call'}
              </button>
            </div>
            <p className="text-xs text-[#9aa3b2]">
              Any phone format is okay — we&apos;ll auto-clean it.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BPOStartPage;
